import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  Heading,
  Icon,
  Spinner,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FiCheckCircle } from 'react-icons/fi';
import { FormField } from '@/design-system/components/FormField';
import { useSubmitDemoRequest } from '@/lib/sdk/email/queries';
import type { DemoRequestDto } from '@/lib/sdk/email/types';
import { getVisitSource } from '@/lib/analytics/visitSource';
import { demoRequestEventProperties } from '@/lib/analytics/demoRequestEvent';
import { trackEvent } from '@/lib/analytics/track';

/**
 * Shared "Request a Demo" form used across marketing/landing pages.
 * Every submit is recorded through the demo-request API first — that row is the
 * lead's durable record, since emails and WhatsApp chats both get deleted. Mobile
 * then hands off to a prefilled WhatsApp chat; desktop shows the success state.
 */

const DEMO_WA_NUMBER = '919890003352';

interface IndustryOption {
  value: string;
  label: string;
}

const DEFAULT_INDUSTRY_OPTIONS: IndustryOption[] = [
  { value: 'Restaurants', label: 'Restaurants / Hospitality' },
  { value: 'Manufacturing', label: 'Manufacturing / Shop Floor' },
  { value: 'Events Management', label: 'Events Management' },
  { value: 'Field Sales', label: 'Field Sales / Field Agents' },
  { value: 'Construction', label: 'Construction / Development' },
  { value: 'Healthcare', label: 'Healthcare / Hospital' },
  { value: 'Airports', label: 'Airports' },
  { value: 'Other', label: 'Other' },
];

// Optional: the browser's referrer names Google but never the search, so this is the
// only place a lead can tell us they heard of TaskEye from a colleague or an AI assistant.
const HEARD_ABOUT_OPTIONS: IndustryOption[] = [
  'Google search',
  'ChatGPT or another AI assistant',
  'A colleague or customer',
  'LinkedIn',
  'WhatsApp message',
  'Other',
].map(option => ({ value: option, label: option }));

// Counts digits rather than matching a format, so "+91 98900 03352", "06.12.34.56.78" and
// "020 7946 0958 ext 12" all get through. Required because a lead we cannot call back is
// not a lead; 7 to 15 digits covers local numbers up to the longest international ones.
const isPhoneNumber = (value: string): boolean => {
  const digits = value.replace(/\D/g, '').length;
  return digits >= 7 && digits <= 15;
};

const demoRequestSchema = z.object({
  full_name: z.string().trim().min(1, 'Please enter your name').max(100, 'Please keep this under 100 characters'),
  company: z.string().trim().min(1, 'Please enter your company').max(150, 'Please keep this under 150 characters'),
  industry: z.string().min(1, 'Please select your industry'),
  phone: z
    .string()
    .trim()
    .min(1, 'Please enter your phone number')
    .refine(isPhoneNumber, 'Please enter a valid phone number'),
  requirements: z.string().trim().max(2000, 'Please keep this under 2000 characters'),
  heard_about: z.string(),
});

type DemoRequestFormValues = z.infer<typeof demoRequestSchema>;

interface DemoRequestFormProps {
  /** Dropdown options for the Industry field. Ignored when `fixedIndustry` is set. */
  industryOptions?: IndustryOption[];
  /**
   * Submits this value as the industry without rendering the field at all — use
   * on a product landing page (e.g. fixedIndustry="Instant Tasks") where the page
   * itself already establishes what the lead came for. Rendering it as a locked
   * "Industry" input instead shows the visitor a product name where an industry
   * belongs, which reads as broken data rather than a helpful default.
   */
  fixedIndustry?: string;
  /**
   * Overrides the submit button label so a page can repeat its own CTA wording
   * here instead of competing with it. Defaults to the site-wide "Request Demo".
   */
  submitLabel?: string;
}

export function DemoRequestForm({
  industryOptions = DEFAULT_INDUSTRY_OPTIONS,
  fixedIndustry,
  submitLabel = 'Request Demo',
}: DemoRequestFormProps) {
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<DemoRequestFormValues>({
    resolver: zodResolver(demoRequestSchema),
    defaultValues: {
      full_name: '',
      company: '',
      industry: fixedIndustry ?? '',
      phone: '',
      requirements: '',
      heard_about: '',
    },
  });
  const submitDemoRequest = useSubmitDemoRequest();

  const isMobile = useBreakpointValue({ base: true, md: false });

  const onSubmit = async (values: DemoRequestFormValues) => {
    // Read at submit, not from defaultValues, so the industry follows the page even if
    // the form is reused without remounting.
    const industry = fixedIndustry ?? values.industry;
    const payload: DemoRequestDto = {
      full_name: values.full_name,
      company: values.company,
      industry,
      phone: values.phone,
      requirements: values.requirements || undefined,
      source: isMobile ? 'whatsapp' : 'email',
      heard_about: values.heard_about || undefined,
      ...getVisitSource(),
    };

    try {
      await submitDemoRequest.mutateAsync(payload);
      trackEvent('demo_requested', demoRequestEventProperties(payload));
    } catch {
      trackEvent('demo_request_failed', demoRequestEventProperties(payload));
      // Desktop shows the mutation's error below the form. Mobile falls through to the
      // hand-off: a failed recording is ours to chase up, but blocking the visitor from
      // reaching us on WhatsApp loses the lead outright.
      if (!isMobile) {
        return;
      }
    }
    if (!isMobile) {
      return;
    }

    const lines = [
      '🎯 *New Demo Request — TaskEye*',
      '',
      `*Name:* ${values.full_name}`,
      `*Company:* ${values.company}`,
      `*Industry:* ${industry}`,
      `*Phone:* ${values.phone}`,
      values.heard_about ? `*Heard via:* ${values.heard_about}` : null,
      // Last, and on its own line: it is the only free-text field, so keeping it out of the
      // label/value run above stops a multi-line answer from swallowing the fields after it.
      values.requirements ? `\n*Requirements:*\n${values.requirements}` : null,
    ].filter((line): line is string => line !== null);
    // Navigate rather than window.open: the await above has already ended the user-gesture
    // window, so a popup would be blocked on mobile Safari and Chrome.
    window.location.assign(`https://wa.me/${DEMO_WA_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`);
  };

  if (submitDemoRequest.isSuccess) {
    return (
      <VStack spacing={4} py={8} textAlign="center" data-testid="demo-success">
        <Icon as={FiCheckCircle} w={16} h={16} color="brand.500" />
        <Heading size="md" color="text-primary">
          Request Received!
        </Heading>
        <Text color="text-secondary" maxW="sm">
          Thanks, {getValues('full_name').trim().split(' ')[0]}! We'll be in touch within 24 hours to
          schedule your personalised demo.
        </Text>
      </VStack>
    );
  }

  return (
    <Box as="form" w="full" noValidate onSubmit={handleSubmit(onSubmit)} data-testid="demo-form">
      <VStack spacing={4} w="full">
        <FormField
          label="Full Name"
          name="full_name"
          register={register}
          error={errors.full_name}
          required
          testId="demo-full-name"
        />
        <FormField
          label="Company Name"
          name="company"
          register={register}
          error={errors.company}
          required
          testId="demo-company"
        />
        {!fixedIndustry && (
          <FormField
            label="Industry"
            name="industry"
            type="select"
            register={register}
            error={errors.industry}
            options={industryOptions}
            placeholder="Select your industry"
            required
            testId="demo-industry"
          />
        )}
        <FormField
          label="Phone Number"
          name="phone"
          type="tel"
          register={register}
          error={errors.phone}
          required
          testId="demo-phone"
        />
        <FormField
          label="Requirements / Notes"
          name="requirements"
          type="textarea"
          register={register}
          error={errors.requirements}
          placeholder="Tell us what you're looking to track or automate"
          testId="demo-requirements"
        />
        <FormField
          label="How did you hear about us?"
          name="heard_about"
          type="select"
          register={register}
          error={errors.heard_about}
          options={HEARD_ABOUT_OPTIONS}
          placeholder="Select an option"
          testId="demo-heard-about"
        />
        {submitDemoRequest.isError && !isMobile && (
          <Text color="red.500" fontSize="sm" textAlign="center" data-testid="demo-error">
            Something went wrong. Please try again or reach out to us directly on WhatsApp.
          </Text>
        )}
        <Button
          type="submit"
          colorScheme="brand"
          size="lg"
          w="full"
          isLoading={submitDemoRequest.isPending}
          spinner={<Spinner size="sm" />}
          loadingText="Sending..."
          data-testid="demo-submit"
        >
          {isMobile ? `${submitLabel} via WhatsApp` : submitLabel}
        </Button>
      </VStack>
    </Box>
  );
}
